import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Channel,
  ErrorResponse,
  GET_CHANNEL_BY_ID,
  GET_CHANNEL_IDEA,
  GET_CHANNEL_IDEAS_BY_PHASE,
  GET_CHANNELS,
  GET_IDEA_REVIEW_REQUEST,
  GET_ME,
  Idea,
  Me,
  PATCH_IDEA_PHASE,
  SETTLE_IDEA_REVIEWER_REQUEST,
  User,
} from '@frontend/data-access';
import { App, Modal, Skeleton } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { DropResult } from '@hello-pangea/dnd';
import {
  selectChannel,
  selectChannelFilters,
  selectIdeaSortby,
  selectLoadingStates,
  setChannel,
  setChannelFilters,
  setChannelPhaseIdeaById,
  setLoadingStates,
} from '../channel-store/channel-store';
import {
  useAppDispatch,
  useAppSelector,
} from '../channel-store/settings/hooks';
import KanbanBoard from './board';
import { handleDragEnd } from './helper';
import IdeaChannelReviewers from './reviewers';

/* eslint-disable-next-line */
export interface KanbanProps {}

export const CHANNEL_DEFAULT_LIMIT = 9;

export function Kanban() {
  const { notification, modal } = App.useApp();
  const { t } = useTranslation('common');
  const router = useRouter();
  const { id, action, ideaId, challengeId } = router.query;
  const dispatch = useAppDispatch();
  const channel = useAppSelector(selectChannel);
  const channelFilters = useAppSelector(selectChannelFilters);
  const loadingStates = useAppSelector(selectLoadingStates);
  const sortby = useAppSelector(selectIdeaSortby);
  const [getIdea] = useLazyQuery<{
    channelIdea: Idea;
  }>(GET_CHANNEL_IDEA);

  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const [getPhaseData] = useLazyQuery<{
    channelIdeasByPhase: {
      items: Idea[];
      total: number;
    };
  }>(GET_CHANNEL_IDEAS_BY_PHASE, {
    fetchPolicy: 'network-only',
  });
  const [getData, { data, loading }] = useLazyQuery<{
    channel: Channel;
  }>(GET_CHANNEL_BY_ID, {
    onCompleted(data) {
      dispatch(setChannel({ ...data.channel }));
    },
  });

  // Ref to store the current AbortController
  const currentAbortController = useRef<AbortController | null>(null);

  const handleRequestCompleted = (data: {
    getIdeaReviewRequest: {
      id: string;
      user: User;
      idea: Idea;
    };
  }) => {
    if (data?.getIdeaReviewRequest.id) {
      Modal.destroyAll();
      modal.confirm({
        width: 500,
        title: (
          <div>
            You have been invited to review and manage{' '}
            <span className="text-primary-600">
              {data?.getIdeaReviewRequest.idea.name}
            </span>{' '}
            idea
          </div>
        ),
        icon: false,
        okText: t('accept'),
        okButtonProps: {
          shape: 'round',
        },
        cancelText: t('reject'),
        cancelButtonProps: {
          shape: 'round',
          danger: true,
        },
        async onOk() {
          await settleIdeaRequest({
            variables: {
              input: {
                ideaId: ideaId,
                response: true,
                userId: meData?.me.id,
              },
            },
          });
        },
        async onCancel() {
          await settleIdeaRequest({
            variables: {
              input: {
                ideaId: ideaId,
                response: false,
                userId: meData?.me.id,
              },
            },
          });
        },
      });
    }
  };

  const [getRequestExpires, { data: reviewReqData }] = useLazyQuery(
    GET_IDEA_REVIEW_REQUEST,
    {
      onCompleted: handleRequestCompleted,
    }
  );

  const handleSettleIdeaRequestCompleted = () => {
    router.push({
      pathname: '/idea-hub',
      query: {
        id: reviewReqData?.getIdeaReviewRequest.idea.channel.id,
      },
    });
  };

  const [settleIdeaRequest] = useMutation(SETTLE_IDEA_REVIEWER_REQUEST, {
    refetchQueries: [
      { query: GET_CHANNEL_BY_ID, variables: { id } },
      { query: GET_CHANNELS },
    ],
    onCompleted: handleSettleIdeaRequestCompleted,
    onError: (error: ErrorResponse) => {
      notification.error({
        message: t('error'),
        description: error.message,
      });
    },
  });

  const handleStageChangeCompleted = (data: { patchIdeaPhase: string }) => {
    if (data?.patchIdeaPhase) {
      notification.success({
        message: t('phase_update_successful'),
      });
    }
  };

  const handleStageChangeError = (error: ErrorResponse) => {
    notification.error({
      message: t('phase_update_error'),
      description: error.message,
    });
  };

  const [phaseChange] = useMutation(PATCH_IDEA_PHASE, {
    onCompleted: handleStageChangeCompleted,
    onError: handleStageChangeError,
  });

  const onItemDragEnd = async (result: DropResult) => {
    try {
      if (channel) {
        if (
          !result.destination ||
          Object.keys(loadingStates).some((key) => loadingStates[key])
        ) {
          return;
        }
        const updatedData = await handleDragEnd(result, channel);
        if (updatedData) {
          const ideaId = result.draggableId;
          dispatch(setChannel(updatedData));
          const { data: patchData } = await phaseChange({
            variables: {
              ideaId: ideaId,
              phaseId: result.destination?.droppableId,
              channelId: id,
            },
          });
          if (patchData?.patchIdeaPhase) {
            const { data } = await getIdea({
              variables: {
                ideaId: ideaId,
              },
              fetchPolicy: 'no-cache',
            });
            const updatedChannel = {
              ...updatedData,
              channelPhases: updatedData.channelPhases.map((channelPhase) => ({
                ...channelPhase,
                ideas: {
                  ...channelPhase.ideas,
                  items: channelPhase.ideas.items.map((idea) =>
                    idea.id === data?.channelIdea.id
                      ? {
                          ...idea,
                          age: data?.channelIdea.age,
                        }
                      : idea
                  ),
                },
              })),
            };
            dispatch(setChannel({ ...updatedChannel }));
          } else {
            fetchData();
          }
        }
      }
    } catch (error) {
      console.log(error);
      notification.error({
        message: t('stage_update_error'),
        description: t('stage_update_error_description'),
      });
    }
  };

  const fetchData = async () => {
    if (!data?.channel?.channelPhases) return;
    if (currentAbortController.current) {
      currentAbortController.current.abort();
    }
    data.channel.channelPhases.forEach((phase) => {
      dispatch(setLoadingStates({ [phase.phase.id]: true }));
    });

    // Create a new AbortController for this fetch
    const abortController = new AbortController();
    currentAbortController.current = abortController;

    for (const phase of data.channel.channelPhases) {
      const phaseId = phase.phase.id;
      dispatch(setLoadingStates({ [phaseId]: true }));
      try {
        await getPhaseData({
          variables: {
            channelId: id,
            phaseId: phase.phase.id,
            page: 1,
            limit: CHANNEL_DEFAULT_LIMIT,
            filterBy: {
              ...channelFilters,
            },
            isAsc: sortby === 'ASC',
          },
          context: {
            fetchOptions: {
              signal: abortController.signal, // Attach abort signal
            },
          },
        }).then(({ data }) => {
          dispatch(
            setChannelPhaseIdeaById({
              ...phase,
              ideas: {
                items: data?.channelIdeasByPhase.items || [],
                total: data?.channelIdeasByPhase.total || 0,
              },
            })
          );
        });

        if (abortController.signal.aborted) {
          return;
        }
      } catch (error) {
        console.error('Error in fetch data');
      } finally {
        dispatch(setLoadingStates({ [phaseId]: false }));
      }
    }
  };

  useEffect(() => {
    switch (action) {
      case 'reviewerInvite': {
        if (ideaId) {
          getRequestExpires({
            variables: {
              ideaId,
            },
          });
        }
        break;
      }
      default:
        break;
    }
  }, [action, getRequestExpires, ideaId]);

  useEffect(() => {
    if (challengeId) {
      dispatch(
        setChannelFilters({
          ...channelFilters,
          challengeIds: [challengeId as string],
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeId]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, channelFilters, sortby]);

  useEffect(() => {
    if (id) {
      getData({ variables: { id } });
    }
  }, [getData, id]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        {loading ? (
          <Skeleton.Input size="small" active className="rounded-lg" />
        ) : (
          <div className="text-gray-900 text-sm font-medium">
            {channel?.title || t('unknown_channel')}{' '}
            <span className="text-gray-700">({channel?.ideasCount || 0})</span>
          </div>
        )}
        <IdeaChannelReviewers channelId={id as string} />
      </div>
      <div className="flex">
        <KanbanBoard onItemDragEnd={onItemDragEnd} />
      </div>
    </div>
  );
}

export default Kanban;
